import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { serviceCityHeadScripts } from "@/lib/cityJsonLd";
import { pergolaBreadcrumbScript } from "@/lib/pergolaJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { getCityHub, CITY_HUBS, type CityHub } from "@/data/cityHubs";
import hero from "@/assets/original/stock-traditional.jpg";

import heroPicture from "@/assets/original/stock-traditional.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
export const Route = createFileRoute("/pergola-covers/in/$city")({
  beforeLoad: ({ params }) => {
    const hub = getCityHub(params.city);
    if (!hub) throw notFound();
  },
  loader: ({ params }) => {
    const hub = getCityHub(params.city);
    if (!hub) throw notFound();
    return { hub };
  },
  head: (ctx) => {
    const hub = getCityHub(ctx.params.city);
    if (!hub) return {};
    // Title ≤60 chars — targets "patio covers {city}" (high-volume,
    // KDI 7 — biggest local opportunity per Semrush).
    const title = hub.slug === "san-antonio" ? "San Antonio Pergola Covers & Replacement Tops | Texas Made Shade" : `Patio & Pergola Covers in ${hub.name}, TX | Texas Made Shade`;
    // Keep under the 160-char meta-description limit after the shared local
    // keyword/city suffix is appended by pageMeta().
    const description = `Custom patio, gazebo & pergola covers in ${hub.name}, TX — welded aluminum frames with Commercial 95 and Phifertex Plus tops. Free quotes.`;
    return {
      links: [canonicalLink(`/pergola-covers/in/${hub.slug}`)],
      meta: routeMeta(ctx, {
        title,
        description,
        image: hero,
        imageAlt: `Custom patio cover with pergola top in ${hub.name}, TX`,
        socialTitle: `Patio & Pergola Covers in ${hub.name}, TX`,
        socialDescription: description,
        keywords: [
          `patio covers ${hub.name} TX`,
          `pergola cover ${hub.name}`,
          `gazebo cover ${hub.name} TX`,
          `patio cover contractor ${hub.name}`,
          `motorized louvered pergola ${hub.name}`,
          `louvered patio cover ${hub.name} TX`,
          `aluminum pergola ${hub.name}`,
          `outdoor living ${hub.name} TX`,
          `pergola financing ${hub.name}`,
          "Acorn Finance pergola",
          `shade structure ${hub.region}`,
          "Commercial 95 patio cover",
          "Phifertex Plus pergola top",
          "custom welded aluminum patio cover Texas",
          "Seguin TX patio cover manufacturer",
        ].join(", "),
      }),
      scripts: [
        ...serviceCityHeadScripts({
          path: `/pergola-covers/in/${hub.slug}`,
          serviceName: "Patio & Pergola Covers",
          city: hub.name,
          region: hub.region,
          parentPath: "/pergola-covers",
          parentName: "Patio & Pergola Covers",
        }),
        // Drop serviceHeadScripts' flat breadcrumb; emit the hierarchical
        // Home → Patio & Pergola Covers → City trail instead.
        ...serviceHeadScripts({
          path: `/pergola-covers/in/${hub.slug}`,
          serviceName: `Patio & Pergola Covers in ${hub.name}, TX`,
          breadcrumbName: `Patio Covers in ${hub.name}`,
          description,
        }).slice(0, 2),
        pergolaBreadcrumbScript(
          `/pergola-covers/in/${hub.slug}`,
          `Patio Covers in ${hub.name}`,
          [{ name: "Patio, Gazebo & Pergola Covers", path: "/pergola-covers" }],
        ),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Patio & Pergola Covers in ${hub.name}, TX`,
            serviceType: "Patio Cover Installation",
            url: canonicalUrl(`/pergola-covers/in/${hub.slug}`),
            provider: { "@id": "https://texasmadeshade.com/#business" },
            areaServed: {
              "@type": "City",
              name: hub.name,
              containedInPlace: { "@type": "AdministrativeArea", name: hub.region },
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-4xl font-bold text-navy-deep">City not found</h1>
      <p className="mt-4 text-navy-deep/75">
        We don't have a dedicated patio cover page for that city yet.{" "}
        <Link to="/pergola-covers" className="text-red underline">See our main patio covers page</Link>.
      </p>
    </main>
  ),
  component: PergolaCoversCityPage,
});

function PergolaCoversCityPage() {
  const { hub } = Route.useLoaderData() as { hub: CityHub };
  const nearby = hub.nearby
    .map((s) => getCityHub(s))
    .filter((c): c is CityHub => Boolean(c));

  return (
    <ServicePage
      currentPath={`/pergola-covers/in/${hub.slug}`}
      serviceName={`Patio & Pergola Covers in ${hub.name}`}
      breadcrumbs={[
        { label: "Patio & Pergola Covers", to: "/pergola-covers" },
        { label: `Patio Covers in ${hub.name}, TX` },
      ]}
      eyebrow={`★ PATIO COVERS · ${hub.name.toUpperCase()}, TX ★`}
      title={hub.slug === "san-antonio" ? "Pergola Covers & Replacement Tops in San Antonio" : `Custom Patio & Pergola Covers in ${hub.name}, TX`}
      intro={`Welded aluminum patio covers, freestanding pavilions and replacement pergola tops for ${hub.name} backyards and outdoor kitchens. Built in our Seguin shop and installed by the same crew that built them.`}
      heroImage={heroPicture}
      heroCta={{
        eyebrow: `Patio Cover Builder · Serving ${hub.name}, TX`,
        headline: `${hub.name} Patio Cover Quote — Free On-Site Estimate`,
        subhead: `Send a few photos of your ${hub.name} backyard and rough dimensions. We'll review the structure, cover material and installation details before confirming your estimate.`,
        primaryHref: "#lets-talk",
        primaryLabel: `Get My ${hub.name} Patio Cover Quote`,
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        `Custom patio cover design in ${hub.name}, TX`,
        "Welded aluminum frames (no rust, no rot)",
        "Commercial 95 + Phifertex Plus shade tops",
        "Attached, freestanding, gable & flat designs",
        "Outdoor kitchen and grill island shade",
        `Serving ${hub.name} and surrounding ${hub.region}`,
      ]}
      body={[
        {
          heading: `Patio Covers Built for ${hub.name} Sun`,
          text: `Choose the cover for the conditions at your ${hub.name} property. Mesh tops allow airflow and filtered light; fabric tops create softer shade. An existing frame should be assessed before a replacement top is fitted.`,
        },
        {
          heading: `Pergola Covers & Replacement Tops for ${hub.name}`,
          text: `Already have a pergola? We build rod-pocket Sunbrella tops, retractable wave canopies, and Phifertex Plus stretched panels to fit your existing frame — measured in ${hub.name} and sewn in Seguin.`,
          cta: { label: "See Pergola Cover Options", href: "/pergola-covers" },
        },
        {
          heading: `Outdoor Kitchen & Pavilion Shade in ${hub.name}`,
          text: `Need shade over the grill, smoker or outdoor sink? We design freestanding aluminum pavilions for ${hub.name} outdoor kitchens that handle Texas heat and lock in resale value.`,
          cta: { label: "Pool & Spa Shade", href: "/pool-spa" },
        },
      ]}
      applications={[
        "Backyard Patio Covers",
        "Pergola Replacement Tops",
        "Freestanding Pavilions",
        "Outdoor Kitchen Shade",
        "Pool Cabanas",
        "Front Porch Covers",
        "Drop Curtains & Wind Walls",
      ]}
      faqs={[
        {
          q: `How much does a patio cover cost in ${hub.name}?`,
          a: `Patio and pergola cover pricing in ${hub.name} depends on size, frame condition, material and mounting. Send photos and approximate measurements for a project-specific free quote.`,
        },
        {
          q: `Do you build patio covers in ${hub.name}, TX?`,
          a: `Yes — we install custom welded patio covers throughout ${hub.name} and ${hub.region}. Free on-site measurements.`,
        },
        {
          q: "Aluminum vs. wood pergola — which is better in Texas?",
          a: "Aluminum avoids rot and can suit a low-maintenance frame. Wood offers a different appearance and needs appropriate upkeep. We can discuss a new frame or a replacement top for an existing structure.",
        },
        {
          q: `Can you replace just the top of my existing ${hub.name} pergola?`,
          a: `Yes — we build replacement Sunbrella rod-pocket covers, retractable wave canopies and Phifertex Plus stretched panels for existing wood or aluminum pergola frames in ${hub.name}.`,
        },
      ]}
      cityCallouts={[
        {
          to: `/${hub.slug}` as string,
          city: hub.name,
          anchorText: `All Texas Made Shade services in ${hub.name}, TX`,
          blurb: `See every awning, cover, sail-shade and welding service we offer to ${hub.name}.`,
        },
        ...nearby.slice(0, 3).map((n) => ({
          to: `/pergola-covers/in/${n.slug}` as string,
          city: n.name,
          anchorText: `Patio covers in ${n.name}, TX`,
          blurb: `We also build custom patio and pergola covers in nearby ${n.name} (${n.region}).`,
        })),
      ]}
      postCta={(
        <section className="border-t-4 border-navy-deep bg-white py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="text-center">
              <div className="font-display text-sm tracking-[0.3em] text-red">★ MORE CITIES WE BUILD PATIO COVERS IN</div>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                Patio & Pergola Covers Across Texas
              </h2>
            </div>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {CITY_HUBS.filter((c) => c.slug !== hub.slug).map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/pergola-covers/in/$city"
                    params={{ city: c.slug }}
                    className="block border-l-4 border-red bg-cream px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    Patio Covers in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    />
  );
}
